import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiClient } from '../services/http.js';
import { useToast } from '../hooks/useToast.jsx';
import { useConfirm } from '../hooks/useConfirm.jsx';
import { ErrorRetryCard } from '../components/common/ErrorRetryCard.jsx';

export default function TrailDetailPage() {
  const { trailId } = useParams();
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const { showApiErrorToast } = useToast();
  const confirm = useConfirm();

  const fetchDetail = async () => {
    setDetailError(null);
    try {
      const { res, data } = await apiClient.request(`/trails/${trailId}`);
      if (res.ok) {
        setDetail(data);
        return;
      }
      setDetailError(apiClient.parseApiError(data, 'Não foi possível carregar esta trilha.'));
    } catch {
      setDetailError('Falha de conexão ao carregar esta trilha.');
    }
  };

  useEffect(() => { setTimeout(fetchDetail, 0); }, [trailId]);

  const runAction = async (path, method, errorMsg) => {
    setBusy(true);
    try {
      const { res, data } = await apiClient.request(`/trails/${trailId}${path}`, { method });
      if (res.ok) {
        await fetchDetail();
      } else {
        showApiErrorToast(res, data, errorMsg);
      }
    } catch {
      showApiErrorToast(null, null, errorMsg);
    }
    setBusy(false);
  };

  const handleSubscribe = () => runAction('/subscribe', 'POST', 'Não foi possível se inscrever nesta trilha.');
  const handlePause = () => runAction('/pause', 'POST', 'Não foi possível pausar a trilha.');
  const handleResume = () => runAction('/resume', 'POST', 'Não foi possível retomar a trilha.');
  const handleUnsubscribe = async () => {
    if (!(await confirm({
      title: 'Cancelar Inscrição',
      message: 'Cancelar sua inscrição nesta trilha? Seu progresso será reiniciado se você se inscrever novamente.',
      variant: 'danger',
      confirmText: 'Cancelar Inscrição'
    }))) return;
    runAction('/subscribe', 'DELETE', 'Não foi possível cancelar a inscrição.');
  };

  if (detail === null) {
    return (
      <div className="min-h-screen bg-surface text-on-surface p-6">
        <div className="max-w-2xl mx-auto">
          {detailError ? (
            <ErrorRetryCard message={detailError} onRetry={fetchDetail} />
          ) : (
            <p className="text-sm text-on-surface-variant">Carregando...</p>
          )}
        </div>
      </div>
    );
  }

  const sub = detail.subscription;
  const isActive = sub?.active;
  const isPaused = isActive && sub.paused;
  const isCompleted = sub && !sub.active && sub.completed_at;
  const progressPct = sub ? Math.min(100, Math.round(((sub.current_day - 1) / detail.day_count) * 100)) : 0;

  return (
    <div className="min-h-screen bg-surface text-on-surface p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <Link to="/trilhas" className="inline-flex items-center gap-2 px-4 py-2 bg-surface-container-low hover:bg-surface-container-high rounded-lg font-medium transition-colors border border-outline-variant">
          <span>←</span> Voltar para Trilhas
        </Link>

        <div>
          <h1 className="text-2xl font-bold">{detail.title}</h1>
          <p className="text-on-surface-variant mt-1">{detail.description}</p>
          <p className="text-xs text-on-surface-variant/70 mt-1">{detail.day_count} dias · 1 e-mail por dia</p>
        </div>

        {sub && (
          <div className="bg-surface-container-low border border-outline-variant rounded-lg p-4">
            <div className="flex justify-between text-xs text-on-surface-variant mb-1">
              <span>
                {isCompleted ? 'Trilha concluída' : `Dia ${sub.current_day} de ${detail.day_count}`}
                {isPaused && ' · Pausada'}
              </span>
              <span>{progressPct}%</span>
            </div>
            <div className="bg-surface-container-highest rounded-full h-2 overflow-hidden">
              <div className="bg-primary h-full" style={{ width: `${progressPct}%` }}></div>
            </div>
          </div>
        )}

        {/* Próximo conteúdo / ementa */}
        {detail.next_content && (
          <div className="bg-surface-container-low border border-outline-variant rounded-lg p-4 space-y-2">
            <h2 className="font-bold text-sm text-on-surface-variant uppercase tracking-wide">
              {sub ? 'Próximo conteúdo' : 'Prévia do primeiro dia'}
            </h2>
            <p className="font-semibold">{detail.next_content.title}</p>
            <p className="text-sm whitespace-pre-wrap text-on-surface-variant">{detail.next_content.text}</p>
          </div>
        )}

        {/* Ações */}
        <div className="flex justify-end gap-2">
          {isActive ? (
            <>
              {isPaused ? (
                <button onClick={handleResume} disabled={busy} className="px-4 py-2 bg-primary text-on-primary font-bold rounded hover:opacity-90 disabled:opacity-50">
                  Retomar
                </button>
              ) : (
                <button onClick={handlePause} disabled={busy} className="px-4 py-2 bg-surface-container-high text-on-surface font-bold rounded hover:opacity-90 disabled:opacity-50">
                  Pausar envios
                </button>
              )}
              <button onClick={handleUnsubscribe} disabled={busy} className="px-4 py-2 bg-error bg-opacity-10 text-error border border-error border-opacity-25 font-bold rounded hover:bg-opacity-20 transition-all disabled:opacity-50">
                Cancelar inscrição
              </button>
            </>
          ) : (
            <button onClick={handleSubscribe} disabled={busy} className="px-4 py-2 bg-primary text-on-primary font-bold rounded hover:opacity-90 disabled:opacity-50">
              {isCompleted ? 'Refazer trilha' : 'Inscrever-se'}
            </button>
          )}
        </div>

        {/* Histórico de dias concluídos */}
        {detail.history && detail.history.length > 0 && (
          <div className="space-y-2">
            <button
              onClick={() => setShowHistory((v) => !v)}
              className="text-sm text-primary hover:underline font-medium"
            >
              {showHistory ? 'Ocultar' : 'Ver'} histórico de dias concluídos ({detail.history.length})
            </button>
            {showHistory && (
              <div className="space-y-2">
                {detail.history.map((day) => (
                  <div key={day.day} className="bg-surface-container-low border border-outline-variant rounded-lg p-3">
                    <p className="text-xs text-on-surface-variant mb-1">Dia {day.day} · {day.title}</p>
                    <p className="text-sm whitespace-pre-wrap">{day.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
